import { useEffect, useRef, useState } from "react";

/** Nextjs */
import dynamic from "next/dynamic";

/** MUI */
import { Alert, Box, Button, Fab, NoSsr, alpha, useMediaQuery } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { SimpleTreeView as TreeView, useTreeViewApiRef } from "@mui/x-tree-view";
/** - icons */
import { Close, Done } from "@mui/icons-material";

/** Redux */
import {
    getAreaForPanel,
    getFetched,
    setLeftDrawerOpen,
    setPrediction,
    setTableOrChart,
} from "@/src/_appStore/DB_Common";
import { clear as clearDates, setDatesSelected } from "@/src/_appStore/DB_Dates";
import * as ChartsFS from "@/src/_appStore/DB_Filters_Charts";
import * as TableFS from "@/src/_appStore/DB_Filters_Table";
import {
    getCurrentIndicator,
    getExpanded,
    getScrolled,
    getSelectedIndicators,
    setCurrentIndicator,
    setExpanded,
    setScrolled,
    setSelectedIndicators,
} from "@/src/_appStore/DB_Indicators";
import { setSelectedTerritorialUnitsItem } from "@/src/_appStore/DB_JTs";
import * as TableLists from "@/src/_appStore/DB_Lists_Table";
import * as ChartsBarsSets from "@/src/_appStore/DB_Settings_Chart_Bars";
import { clear as clearTableSettings } from "@/src/_appStore/DB_Settings_Table";
import { useDispatch, useSelector } from "react-redux";

/** API */
import { getIndicatorsTreeFromAPI } from "@/src/_APIs";

/** Helpers */
import useHelper from "@/src/_common/useHelper";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import clsx from "clsx";
import _ from "lodash";
import { useTranslation } from "react-i18next";

/** UI */
import DrawerPreloader from "../components/DrawerPreloader";

// import IndicatorsXTreeItem from "./IndicatorsXTreeItem";
import Certificate from "@/src/pages/_Commons/Certificate/Certificate";
import IndicatorsXTreeItem from "./IndicatorsXTreeItemCustom";
import MultiSelect from "./MultiSelect";
import QuickNav from "./QuickNav";
import SronlyH2 from "@/src/components/srOnly/SronlyH2";

const AppTooltip = dynamic(() => import("@/src/_common/AppTooltip"));

const useStyles = makeStyles((theme) => ({
    root: {
        height: "100%",
        flexGrow: 1,
        maxWidth: "100%",
        overflow: "hidden",
        overflowY: "auto",
    },
    treeWrapper: {
        ...theme.shape.boxedWrapper,
        borderRadius: 4,
        height: "calc(100vh - 363px)",
        marginTop: theme.spacing(3),
        padding: theme.spacing(2, 0.5, 2, 1),
        [theme.breakpoints.down("xl")]: {
            height: "calc(100vh - 351px)",
        },
        [theme.breakpoints.down("lg")]: {
            height: "calc(100vh - 315px)",
        },
        [theme.breakpoints.down("md")]: {
            height: "calc(100vh - 284px)",
        },
        [theme.breakpoints.down("sm")]: {
            marginTop: theme.spacing(1),
            height: "calc(100vh - 260px)",
        },
    },
    osScrollbar: {
        height: "calc(100vh - 434px)",

        [theme.breakpoints.down("lg")]: {
            height: "calc(100vh - 320px)",
        },
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 309px)",
            // height: "calc(100vh - 390px)",
            // height: "calc(100vh - 420px)",
        },
        "& > .os-scrollbar > .os-scrollbar-track > .os-scrollbar-handle, & > .os-scrollbar:hover > .os-scrollbar-track > .os-scrollbar-handle":
            {
                background: "#919090",
            },

        "& > .os-scrollbar.os-scrollbar-vertical": {
            padding: 1,
            width: 6,
            transition: "all 0.125s linear",
            background: alpha(theme.palette.primary.main, 0.05),
            borderRadius: 16,
            "&:hover": {
                width: 16,
                padding: 2,
                cursor: "s-resize",
            },
        },
    },
    osScrollbarAndroid: {
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 370px)",
        },
    },
    osScrollbarIOS: {
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 420px)",
        },
    },
    osScrollbarIOSSafari: {
        [theme.breakpoints.down("sm")]: {
            height: "calc(100vh - 400px)",
        },
    },
    treeActionsWrapper: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-end",
        margin: theme.spacing(0),
        paddingTop: theme.spacing(3),
        position: "relative",
        [theme.breakpoints.down("lg")]: {
            paddingTop: theme.spacing(1),
            // position: "fixed",
            backgroundColor: "#fff",
            width: "calc(100% - 16px)",
            // left: 8,
            // bottom: 155,
        },
        [theme.breakpoints.down("sm")]: {
            // position: "relative",
            bottom: "unset",
        },
    },
    clearBtn: {
        minWidth: "unset",
        height: 51,
        width: 51,
        [theme.breakpoints.down("sm")]: {
            height: 40,
            width: 40,
            "& svg": {
                fontSize: "1.11111111rem",
            },
        },
    },
    errorBlockqoute: {
        color: "#F00",
        textAlign: "center",
        "& small": {
            color: theme.palette.text.hint,
        },
    },
    counter: {
        display: "block",
        width: "calc(100% - 24px)",
        textAlign: "right",
        color: "rgba(0, 0, 0, 0.5)",
        fontSize: "0.5rem",
        marginBottom: theme.spacing(1),
        borderBottom: "1px solid #F6F6F6",
        padding: theme.spacing(1),
    },
    clamp: {
        [theme.breakpoints.down("lg")]: {
            width: "100%",
            margin: theme.spacing(0.5, 0),
            padding: theme.spacing(1, 0),
            justifyContent: "end",
            marginLeft: 0,
            "&, & span": {
                fontSize: "0.88888889rem",
            },
        },
        [theme.breakpoints.down("md")]: {
            borderBottom: "1px solid rgba(0, 0, 0, 0.06 )",
            marginLeft: 0,
            "&, & span": {
                fontSize: "0.77777778rem",
            },
        },
    },
    generateBtn: {
        minHeight: 51,
        [theme.breakpoints.down("md")]: {
            height: 40,
            minHeight: 40,
            padding: theme.spacing(1, 1.5),
            fontSize: "0.66666667rem",
        },
    },
}));

const IndicatorsXTree = (props) => {
    const { open, onGetTreeData, onSelection, customerFilters, searchIds, searchRef } = props;

    const classes = useStyles();
    const apiRef = useTreeViewApiRef();
    const treeRef = useRef();
    const resetBtnRef = useRef();
    const applyBtnRef = useRef();

    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const { translationFromJson, getAllIDs } = useHelper();
    const dispatch = useDispatch();
    const { trackEvent } = useMatomo();
    const mdDown = useMediaQuery((theme) => theme.breakpoints.down("md"));

    /** Redux states */
    const _fetched = useSelector(getFetched);
    const areaForPanel = useSelector(getAreaForPanel);
    const expanded = useSelector(getExpanded);
    const selectedIndicators = useSelector(getSelectedIndicators);
    const scrolled = useSelector(getScrolled);
    const currentIndicator = useSelector(getCurrentIndicator);

    /** States */

    const [ERROR, setERROR] = useState(false);
    const [EXPANDED, setEXPANDED] = useState(expanded);
    const [INDICATORS_TREE_FILTERED, setINDICATORS_TREE_FILTERED] = useState([]);
    const [INDICATORS_TREE, setINDICATORS_TREE] = useState([]);
    const [INDICATORS_TREE_FLAT, setINDICATORS_TREE_FLAT] = useState([]);
    const [LOADING, setLOADING] = useState(false);
    const [RESELECTED_INDICATORS, setRESELECTED_INDICATORS] = useState([]);
    const [RESELECTED, setRESELECTED] = useState([]);
    const [SELECTED, setSELECTED] = useState([]);
    const [CERTIFICATE, setCERTIFICATE] = useState(null);
    const [MULTI_SELECT, setMULTI_SELECT] = useState(null);
    const [QUICK_NAV, setQUICK_NAV] = useState(null);

    const scrollBarsRef = useRef();

    /** Effects */
    /** - selectedIndicators > uids  */
    useEffect(() => {
        const _selectedUids = Array.isArray(selectedIndicators)
            ? selectedIndicators?.map((i) => i.uid)
            : [selectedIndicators?.uid];

        setSELECTED(_selectedUids?.toSorted());
    }, [selectedIndicators]);

    /** - set reselected indicators */
    useEffect(() => {
        // clean up controller
        let isSubscribed = true;

        try {
            if (open && !RESELECTED_INDICATORS.length) {
                isSubscribed &&
                    setRESELECTED_INDICATORS(
                        Array.isArray(selectedIndicators) ? [...selectedIndicators] : [selectedIndicators]
                    );
            }
            if (open && !RESELECTED.length) {
                const _selectedUids = Array.isArray(selectedIndicators)
                    ? selectedIndicators?.map((i) => i.uid)
                    : [selectedIndicators?.uid];
                isSubscribed && setRESELECTED(_selectedUids?.toSorted());
            }

            if (!open && RESELECTED_INDICATORS.length) {
                isSubscribed && setRESELECTED_INDICATORS([]);
            }
            if (!open && RESELECTED.length) {
                isSubscribed && setRESELECTED([]);
            }
        } catch (error) {
            console.log(error, { selectedIndicators });
        }

        // cancel subscription to useEffect
        return () => (isSubscribed = false);
    }, [selectedIndicators, open]);

    /** - collect data from API */
    useEffect(
        () => {
            /** matomo */
            let _matomo = {
                category: "Dane",
                action: `Panel - API "getIndicatorsTree"`,
                name: "before",
                documentTitle: document?.title,
                href: window.location.origin + window.location.pathname,
            };

            trackEvent(_matomo);

            // clean up controller
            let isSubscribed = true;
            // if (!_fetched || INDICATORS_TREE.length || ERROR) return;

            isSubscribed && setLOADING(true);
            getIndicatorsTreeFromAPI()
                .then((result) => {
                    // process.env.NODE_ENV === "development" &&
                    //     console.log("%c getIndicatorsTreeFromAPI: ", "background: #000; color:#FFF");

                    if (result.message && result.message.type === "Error") {
                        setERROR(result);

                        /** matomo */
                        _matomo = {
                            category: "Dane",
                            action: `Panel - API "getIndicatorsTree"`,
                            name: "error " + (result?.status || ""),
                            documentTitle: document?.title,
                            href: window.location.origin + window.location.pathname,
                        };
                    } else {
                        /** Is there at least one indicator in a result? */
                        if (result.length) {
                            /** Sort first level by name depending on language */
                            if (lang === "pl") {
                                result.sort((a, b) => a.name.localeCompare(b.name));
                            } else {
                                result.sort((a, b) => a[`name_${lang}`].localeCompare(b[`name_${lang}`]));
                            }

                            setINDICATORS_TREE(result);

                            onGetTreeData(JSON.parse(JSON.stringify(result)));
                            setERROR(false);

                            /** matomo */
                            _matomo = {
                                category: "Dane",
                                action: `Panel - API "getIndicatorsTree"`,
                                name: `success`,
                                value: result?.length,
                                documentTitle: document?.title,
                                href: window.location.origin + window.location.pathname,
                            };
                        } else {
                            /** no indicators */
                            setERROR({
                                message: {
                                    type: "Error",
                                    text: "Lista wskaźników jest pusta. Spróbuj przeładować stronę.",
                                    text_en: "The list of indicators is empty. Try reloading the page.",
                                },
                            });

                            /** matomo */
                            _matomo = {
                                category: "Dane",
                                action: `Panel - API "getIndicatorsTree"`,
                                name: `error (no data)`,
                                value: result?.length,
                                documentTitle: document?.title,
                                href: window.location.origin + window.location.pathname,
                            };
                        }
                    }
                })
                .catch((error) => {
                    console.log("%c getIndicatorsTreeFromAPI error: ", "background: #f00; color:#FFF", error);
                    setERROR({
                        message: {
                            type: "Error",
                            text: "Wykryto błąd",
                            text_en: "Error detected",
                        },
                    });

                    /** matomo */
                    _matomo = {
                        category: "Dane",
                        action: `Panel - API "getIndicatorsTree"`,
                        name: `error (catch)`,
                        documentTitle: document?.title,
                        href: window.location.origin + window.location.pathname,
                    };
                })
                .finally(() => {
                    setLOADING(false);

                    trackEvent(_matomo);
                });

            // cancel subscription to useEffect
            return () => (isSubscribed = false);
        },
        [
            // INDICATORS_TREE, _fetched
        ]
    );

    /** - watch reselected indicators */
    useEffect(() => {
        onSelection(RESELECTED_INDICATORS);
    }, [RESELECTED_INDICATORS]);

    /** - watch expanded */
    useEffect(() => {
        const _areasIDs = INDICATORS_TREE?.filter((i) => i.type === "AREA")?.map((i) => i.id);
        setEXPANDED(expanded?.length ? expanded : _areasIDs);
    }, [expanded, INDICATORS_TREE]);

    /** - watch search IDs */
    useEffect(() => {
        if (!INDICATORS_TREE.length) return;

        // clean up controller
        let isSubscribed = true;

        const _filtered = searchIds.length
            ? JSON.parse(JSON.stringify(INDICATORS_TREE)).filter((i) => searchIds.includes(i.id))
            : JSON.parse(JSON.stringify(INDICATORS_TREE));

        const _flat = getAllIDs(_filtered);

        isSubscribed && setINDICATORS_TREE_FILTERED(_filtered);
        isSubscribed && setINDICATORS_TREE_FLAT(_flat);
        isSubscribed && !!searchIds?.length && setEXPANDED(searchIds);

        // cancel subscription to useEffect
        return () => (isSubscribed = false);
    }, [searchIds, INDICATORS_TREE]);

    /** - check area for panel (from storytelling) */
    useEffect(() => {
        if (!areaForPanel || !INDICATORS_TREE.length) return;

        const _e = getAllIDs(INDICATORS_TREE)
            ?.filter((i) => i.type === "GROUP")
            ?.sort((a, b) => a.name.localeCompare(b.name));

        const _f = _e?.find(
            (f) => f?.group_id?.toString() === areaForPanel?.group_id?.toString() || f?.name === areaForPanel?.name
        );

        const _expandedArea = [];
        _f?.id
            ?.split("-")
            .forEach((a, i) => _expandedArea.push(_expandedArea[i - 1] ? _expandedArea[i - 1] + "-" + a : a));

        dispatch(setExpanded(_expandedArea));
        dispatch(setScrolled(_f?.id));
    }, [areaForPanel, INDICATORS_TREE]);

    /** - scrollTo by search*/
    useEffect(() => {
        if (!scrolled || !apiRef?.current) return;

        setTimeout(() => {
            apiRef.current.focusItem(null, scrolled);
        }, 500);

        setTimeout(() => dispatch(setScrolled(null)), 2500);
    }, [scrolled, apiRef?.current]);

    /** - scrollTo by opening*/
    useEffect(() => {
        if (!open || !currentIndicator?.uid || !apiRef?.current) return;

        setTimeout(() => {
            apiRef.current.focusItem(null, currentIndicator?.uid);
        }, 1000);
    }, [open, currentIndicator, apiRef?.current]);

    /** /end Effects */

    /** Handlers */
    const handleClearCustomerFilters = () => {
        /** Reset view to 'Table' */
        dispatch(setTableOrChart("table"));

        /** Clear JTs */
        dispatch(setSelectedTerritorialUnitsItem([]));

        /** Clear Dates */
        dispatch(setDatesSelected([]));

        /** Clear table filters */
        dispatch(TableFS.clear());

        /** Clear table Lists */
        dispatch(TableLists.clear());

        /** Clear charts filters */
        dispatch(ChartsFS.clear());

        /** Clear bars charts settings */
        dispatch(ChartsBarsSets.clear());
    };

    const handleClearAll = () => {
        dispatch(clearDates());
        dispatch(clearTableSettings());
        dispatch(setExpanded([]));
        dispatch(setSelectedIndicators([]));
        dispatch(setCurrentIndicator(null));
        setRESELECTED_INDICATORS([]);
        setRESELECTED([]);
        handleClearCustomerFilters();

        const _tree = document.getElementById("indicators-tree");
        _tree.scrollTop = 0;
    };

    const handleApply = async () => {
        const trackValue = RESELECTED_INDICATORS?.map(
            (i) => `${i.indicator_id}:"${translationFromJson(i, "name")}"`
        )?.join(";");

        const _newCurrentIndicator = _.maxBy(RESELECTED_INDICATORS, "timestamp");

        await Promise.all([
            dispatch(setPrediction(false)),
            dispatch(setSelectedTerritorialUnitsItem([])),
            dispatch(setDatesSelected([])),

            dispatch(TableFS.setCurrentSection(null)),
            dispatch(TableFS.setDefaults(null)),
            dispatch(ChartsFS.setCurrentSection(null)),
            dispatch(ChartsFS.setDefaults(null)),

            _newCurrentIndicator && dispatch(setCurrentIndicator(_newCurrentIndicator)),

            dispatch(setSelectedIndicators(RESELECTED_INDICATORS)),
            Object.values(customerFilters).some((v) => !v) && handleClearCustomerFilters(),
        ]).then(() => {
            dispatch(setLeftDrawerOpen(false));

            dispatch(setExpanded(EXPANDED));
        });

        const currentHREF = window.location.origin + window.location.pathname;

        trackEvent({
            category: "Dane",
            action: "Panel wyboru - wybór wskaźnika(-ów)",
            name: trackValue,
            value: trackValue,
            documentTitle: document?.title,
            href: currentHREF,
        });
    };

    const handleOnSelectedItemsChange = (event, itemIds, multi = false) => {
        setRESELECTED(itemIds?.toSorted());

        if (multi) {
            const _newReselectedIndicators = INDICATORS_TREE_FLAT?.filter((f) => itemIds?.includes(f.id))?.map(
                (r, i) => {
                    const _itemReselectedTimestamp = RESELECTED_INDICATORS?.find((ri) => ri?.uid === r.id)?.timestamp;

                    const _item = {
                        ...r,
                        id: r.id.split("-").slice(-1)[0]?.toString(),
                        uid: r.id,
                        timestamp: _itemReselectedTimestamp || Date.now() + i,
                    };
                    delete _item.children;

                    return _item;
                }
            );
            setRESELECTED_INDICATORS(_newReselectedIndicators);
        }
    };

    const handleOnItemSelectionToggle = (event, itemId, isSelected) => {
        let _item;
        let _newReselectedIndicators = structuredClone(RESELECTED_INDICATORS);

        if (isSelected) {
            /** Add */
            _item = INDICATORS_TREE_FLAT?.find((i) => i.id === itemId);

            if (_item) {
                _item = {
                    ..._item,
                    id: _item.id.split("-").slice(-1)[0]?.toString(),
                    uid: _item.id,
                    timestamp: Date.now(),
                };
                delete _item.children;
            }

            _newReselectedIndicators?.push(_item);
        } else {
            /** Remove */
            _newReselectedIndicators = _newReselectedIndicators?.filter((r) => r.uid !== itemId);
        }

        setRESELECTED_INDICATORS(_newReselectedIndicators);
    };

    const handleOnNodeToggle = (event, nodeIds) => {
        nodeIds = nodeIds?.length ? nodeIds : ["1366613666"];
        setEXPANDED(nodeIds);
    };

    const handleShowCertificate = (args) => setCERTIFICATE(args || null);

    const handleMultiSelect = (args) => setMULTI_SELECT(args || null);

    const handleQuickNav = (args) => setQUICK_NAV(args || null);

    return (
        <NoSsr>
            {!!LOADING ? (
                <DrawerPreloader />
            ) : !!ERROR ? (
                <Alert severity={"error"} sx={{ width: "100%" }} variant={"outlined"}>
                    {translationFromJson(ERROR.message, "text") || t("Nieznany błąd")}
                </Alert>
            ) : (
                <>
                    <Box ref={scrollBarsRef} id={"DB-iScroll"} className={clsx(classes.treeWrapper)} sx={{ mt: 2 }}>
                        <TreeView
                            ref={treeRef}
                            apiRef={apiRef}
                            className={classes.root}
                            id={"indicators-tree"}
                            expandedItems={EXPANDED ? EXPANDED : []}
                            onExpandedItemsChange={handleOnNodeToggle}
                            multiSelect={true}
                            selectedItems={RESELECTED}
                            onSelectedItemsChange={handleOnSelectedItemsChange}
                            onItemSelectionToggle={handleOnItemSelectionToggle}
                            checkboxSelection
                            aria-label={t("Zmienne")}
                        >
                            {INDICATORS_TREE_FILTERED.map((data) =>
                                renderTree({
                                    nodes: data,
                                    nodeIndex: 0,
                                    lang,
                                    RESELECTED_INDICATORS,
                                    RESELECTED,
                                    INDICATORS_TREE,
                                    EXPANDED,
                                    translationFromJson,
                                    applyBtnRef,
                                    resetBtnRef,
                                    searchRef,
                                    onShowCertificate: handleShowCertificate,
                                    onMultiSelect: handleMultiSelect,
                                    onQuickNav: handleQuickNav,
                                })
                            )}
                        </TreeView>
                    </Box>

                    <SronlyH2>{t("Resetowanie wyboru danych i generowanie zestawienia")}</SronlyH2>
                    <div className={classes.treeActionsWrapper}>
                        <AppTooltip
                            title={
                                !selectedIndicators.length &&
                                JSON.stringify(RESELECTED_INDICATORS) === JSON.stringify(selectedIndicators)
                                    ? ""
                                    : t("Zresetuj wybór")
                            }
                            arrow
                            placement={"top"}
                            type={"dark"}
                            classes={{ tooltip: classes.tooltip }}
                        >
                            <span style={{ marginRight: mdDown ? 16 : 32 }}>
                                <Fab
                                    ref={resetBtnRef}
                                    variant={"outlined"}
                                    color={"primary"}
                                    size={"small"}
                                    className={classes.clearBtn}
                                    onClick={handleClearAll}
                                    disabled={
                                        !SELECTED.length && JSON.stringify(RESELECTED) === JSON.stringify(SELECTED)
                                    }
                                    aria-label={t("Zresetuj wybór dokonany w drzewku")}
                                >
                                    <Close />
                                </Fab>
                            </span>
                        </AppTooltip>

                        <Button
                            ref={applyBtnRef}
                            variant={"contained"}
                            color={"primary"}
                            onClick={handleApply}
                            disabled={!RESELECTED.length}
                            startIcon={mdDown ? null : <Done />}
                            className={classes.generateBtn}
                        >
                            {t("Wygeneruj zestawienie")}
                        </Button>
                    </div>

                    <Certificate
                        indicatorId={CERTIFICATE?.item?.indicator_id}
                        item={CERTIFICATE?.item}
                        show={!!CERTIFICATE}
                        onClose={() => handleShowCertificate(false)}
                        color={"primary"}
                        hideIcon={true}
                    />

                    {!!MULTI_SELECT ? (
                        <MultiSelect
                            item={MULTI_SELECT?.item}
                            anchorEl={MULTI_SELECT.anchorEl}
                            selected={RESELECTED}
                            onClose={() => handleMultiSelect(false)}
                            onSelectIndicators={(e, ids) => handleOnSelectedItemsChange(e, ids, true)}
                        />
                    ) : null}

                    {!!QUICK_NAV ? (
                        <QuickNav
                            item={QUICK_NAV?.item}
                            anchorEl={QUICK_NAV.anchorEl}
                            refs={{ searchRef, applyBtnRef, resetBtnRef }}
                            onClose={() => handleQuickNav(false)}
                        />
                    ) : null}
                </>
            )}
        </NoSsr>
    );
};

export default IndicatorsXTree;

const renderTree = ({
    nodes,
    nodeIndex = 0,
    lang,
    RESELECTED_INDICATORS,
    RESELECTED,
    INDICATORS_TREE,
    EXPANDED,
    translationFromJson,
    applyBtnRef,
    resetBtnRef,
    searchRef,
    onShowCertificate,
    onMultiSelect,
    onQuickNav,
}) => {
    nodeIndex++;

    if (Array.isArray(nodes.children) && nodes.children?.length) {
        /** Sort children by name depending on language */
        if (lang === "pl") {
            nodes.children.sort((a, b) => a.name.localeCompare(b.name));
        } else {
            nodes.children.sort((a, b) => a[`name_${lang}`].localeCompare(b[`name_${lang}`]));
        }
    }
    return (
        <IndicatorsXTreeItem
            key={nodes.id}
            labelText={translationFromJson(nodes, "name").replace(/(<([^>]+)>)/gi, "")}
            nodes={nodes}
            expanded={EXPANDED}
            refs={{ applyBtnRef, resetBtnRef, searchRef }}
            onShowCertificate={onShowCertificate}
            onMultiSelect={onMultiSelect}
            onQuickNav={onQuickNav}
            selectedIndicators={RESELECTED_INDICATORS}
        >
            {Array.isArray(nodes.children) && nodes.children?.length
                ? nodes.children.map((node) =>
                      renderTree({
                          nodes: node,
                          nodeIndex,
                          lang,
                          RESELECTED_INDICATORS,
                          RESELECTED,
                          INDICATORS_TREE,
                          EXPANDED,
                          translationFromJson,
                          applyBtnRef,
                          resetBtnRef,
                          searchRef,
                          onShowCertificate,
                          onMultiSelect,
                          onQuickNav,
                      })
                  )
                : null}
        </IndicatorsXTreeItem>
    );
};
